import * as THREE from 'three';
import { box, mergeParts, part, plane, type Part } from '@/art/geometry';
import { emissive, vertexColored } from '@/art/materials';
import { CITY } from '@/world/grid';

export const ENVIRONMENT = {
  street: {
    width: CITY.streetWidth,
    pavementWidth: CITY.pavementWidth,
    pavementHeight: CITY.pavementHeight,
    laneDashLength: 2.2,
    laneDashGap: 2.8,
    wetPatchCount: 16,
    curbDrainSpacing: 8,
    triBudget: 1200,
  },
  quay: {
    depth: CITY.quayDepth,
    heightAboveWater: CITY.quayHeight,
    wallThickness: 0.6,
    bumperHeight: 0.18,
    slabModule: 2.4,
    mooringRingSpacing: 7.2,
    triBudget: 1500,
  },
  water: {
    canalWidth: CITY.canalWidth,
    waterLevel: CITY.waterLevel,
    subdivisions: 24,
    waveAmplitude: 0.065,
    waveSpeed: 0.95,
    rippleCount: 28,
    reflectionCount: 14,
    rippleHeight: 0.018,
    triBudget: 2200,
  },
  bridge: {
    width: CITY.streetWidth,
    span: CITY.canalWidth,
    deckThickness: 0.35,
    curbWidth: 0.35,
    curbHeight: 0.22,
    railHeight: 0.78,
    railThickness: 0.16,
    postSpacing: CITY.tile,
    triBudget: 980,
  },
} as const;

/**
 * Straatdek: asfalt met stoepranden.
 *
 * Het wegdek is één plat vlak; de stoep is een dunne verhoogde plaat langs de randen. Op
 * isometrische afstand is die 15 cm rand precies genoeg om de rijbaan van het trottoir te
 * scheiden — de reden dat de stijlgids stoepranden overdrijft in plaats van ze weg te laten.
 */

export interface GroundOptions {
  width: number;
  depth: number;
  /** Aan welke zijden een trottoir ligt. */
  pavements: { north?: boolean; south?: boolean; east?: boolean; west?: boolean };
}

export function buildGround(options: GroundOptions): THREE.Mesh {
  const { width, depth, pavements } = options;
  const parts: Part[] = [part(plane(width, depth), 'asphalt')];

  const w = CITY.pavementWidth;
  const h = CITY.pavementHeight;

  if (pavements.north) {
    parts.push(part(box(width, h, w).translate(0, h / 2, -depth / 2 + w / 2), 'pavement'));
  }
  if (pavements.south) {
    parts.push(part(box(width, h, w).translate(0, h / 2, depth / 2 - w / 2), 'pavement'));
  }
  if (pavements.west) {
    parts.push(part(box(w, h, depth).translate(-width / 2 + w / 2, h / 2, 0), 'pavement'));
  }
  if (pavements.east) {
    parts.push(part(box(w, h, depth).translate(width / 2 - w / 2, h / 2, 0), 'pavement'));
  }

  const dash = ENVIRONMENT.street.laneDashLength;
  const gap = ENVIRONMENT.street.laneDashGap;
  for (let x = -width / 2 + dash; x < width / 2 - dash; x += dash + gap) {
    parts.push(part(box(dash, 0.012, 0.12).translate(x, 0.012, 0), 'slateGrey'));
  }

  for (let x = -width / 2 + 4; x < width / 2 - 2; x += ENVIRONMENT.street.curbDrainSpacing) {
    if (pavements.north) {
      parts.push(part(box(0.8, 0.02, 0.08).translate(x, h + 0.012, -depth / 2 + w + 0.12), 'charcoal'));
    }
    if (pavements.south) {
      parts.push(part(box(0.8, 0.02, 0.08).translate(x + 2.1, h + 0.012, depth / 2 - w - 0.12), 'charcoal'));
    }
  }

  for (const x of [-width * 0.32, -width * 0.08, width * 0.18, width * 0.38]) {
    for (const z of [-depth * 0.26, depth * 0.2]) {
      parts.push(part(box(0.58, 0.014, 0.42).translate(x, 0.014, z), 'darkSteel'));
      parts.push(part(box(0.06, 0.018, 0.42).translate(x - 0.14, 0.02, z), 'nightStone'));
      parts.push(part(box(0.06, 0.018, 0.42).translate(x + 0.14, 0.02, z), 'nightStone'));
    }
  }

  for (let i = 0; i < ENVIRONMENT.street.wetPatchCount; i++) {
    const x = -width / 2 + 3.2 + ((i * 11.7) % Math.max(1, width - 6.4));
    const z = -depth / 2 + w + 1.0 + ((i * 5.1) % Math.max(1, depth - w * 2 - 2));
    const patchWidth = 1.1 + (i % 3) * 0.52;
    const patchDepth = 0.16 + (i % 2) * 0.12;
    const color = i % 3 === 0 ? 'sodiumAmber' : 'deepTeal';
    parts.push(part(box(patchWidth, 0.01, patchDepth).translate(x, 0.018, z), color));
  }

  const mesh = new THREE.Mesh(mergeParts(parts), vertexColored());
  mesh.name = 'NA_Env_Ground';
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * Grachtwater op waterpeil.
 *
 * Het vlak heeft net genoeg segmenten voor een subtiele vertex-wave, plus losse lichte
 * rimpelstrepen. Geen shader of reflectie: de stijl blijft donkerteal negatieve ruimte,
 * maar het water leest nu als bewegend oppervlak in plaats van een platte vloer.
 */
export function buildWater(width: number, depth: number): THREE.Group {
  const group = new THREE.Group();
  group.name = 'NA_Env_Water';
  group.position.y = CITY.waterLevel;

  const geometry = new THREE.PlaneGeometry(
    width,
    depth,
    ENVIRONMENT.water.subdivisions,
    Math.max(2, Math.round(ENVIRONMENT.water.subdivisions * depth / width)),
  ).toNonIndexed();
  geometry.rotateX(-Math.PI / 2);
  const mesh = new THREE.Mesh(mergeParts([part(geometry, 'canalWater')]), vertexColored());
  mesh.name = 'surface';
  mesh.receiveShadow = true;
  mesh.userData.naWater = true;
  mesh.userData.naWaterBasePositions = Float32Array.from(mesh.geometry.getAttribute('position').array as Float32Array);
  group.add(mesh);

  for (let i = 0; i < ENVIRONMENT.water.rippleCount; i++) {
    const length = 2.1 + (i % 4) * 0.7;
    const safeX = width / 2 - length / 2 - 0.06;
    const safeZ = depth / 2 - 0.12;
    const ripple = new THREE.Mesh(
      mergeParts([part(box(length, 0.01, 0.055 + (i % 3) * 0.02), 'canalHighlight')]),
      emissive('canalHighlight', 0.6),
    );
    const x = -safeX + ((i * 3.7) % (safeX * 2));
    const z = -safeZ + 0.08 + ((i * 1.85) % Math.max(0.1, safeZ * 2 - 0.16));
    ripple.name = 'ripple';
    ripple.position.set(x, ENVIRONMENT.water.rippleHeight + (i % 2) * 0.008, z);
    ripple.rotation.y = i % 2 === 0 ? 0.08 : -0.1;
    ripple.userData.naWaterRipple = true;
    ripple.userData.naWaterRippleBaseX = x;
    ripple.userData.naWaterRippleBaseY = ripple.position.y;
    ripple.userData.naWaterRippleBaseZ = z;
    ripple.userData.naWaterRipplePhase = i * 0.73;
    group.add(ripple);
  }

  for (let i = 0; i < ENVIRONMENT.water.reflectionCount; i++) {
    const length = 0.7 + (i % 5) * 0.34;
    const safeX = width / 2 - length / 2 - 0.08;
    const safeZ = depth / 2 - 0.2;
    const reflection = new THREE.Mesh(
      mergeParts([part(box(length, 0.01, 0.05), 'sodiumAmber')]),
      emissive('sodiumAmber', 0.42),
    );
    const x = -safeX + ((i * 8.6) % (safeX * 2));
    const z = -safeZ + 0.2 + ((i * 3.3) % Math.max(0.1, safeZ * 2 - 0.4));
    reflection.name = 'reflection';
    reflection.position.set(x, ENVIRONMENT.water.rippleHeight + 0.012, z);
    reflection.rotation.y = i % 2 === 0 ? 0.12 : -0.08;
    reflection.userData.naWaterRipple = true;
    reflection.userData.naWaterRippleBaseX = x;
    reflection.userData.naWaterRippleBaseY = reflection.position.y;
    reflection.userData.naWaterRippleBaseZ = z;
    reflection.userData.naWaterRipplePhase = i * 1.19 + 0.4;
    group.add(reflection);
  }

  return group;
}

export function animateWater(root: THREE.Object3D, elapsed: number): void {
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;

    if (object.userData.naWaterRipple === true) {
      const baseX = object.userData.naWaterRippleBaseX;
      const baseY = object.userData.naWaterRippleBaseY;
      const baseZ = object.userData.naWaterRippleBaseZ;
      const phase = object.userData.naWaterRipplePhase;
      if (
        typeof baseX !== 'number'
        || typeof baseY !== 'number'
        || typeof baseZ !== 'number'
        || typeof phase !== 'number'
      ) {
        return;
      }
      object.position.set(
        baseX + Math.sin(elapsed * 0.34 + phase) * 0.09,
        baseY + Math.sin(elapsed * 1.35 + phase) * 0.012,
        baseZ + Math.cos(elapsed * 0.28 + phase) * 0.07,
      );
      return;
    }

    if (object.userData.naWater !== true) return;
    const base = object.userData.naWaterBasePositions;
    if (!(base instanceof Float32Array)) return;
    const position = object.geometry.getAttribute('position');
    const array = position.array as Float32Array;
    const amplitude = ENVIRONMENT.water.waveAmplitude;
    const speed = ENVIRONMENT.water.waveSpeed;

    for (let i = 0; i < position.count; i++) {
      const x = base[i * 3]!;
      const z = base[i * 3 + 2]!;
      array[i * 3] = x;
      array[i * 3 + 1] = Math.sin(x * 0.42 + elapsed * speed) * amplitude
        + Math.cos(z * 0.58 + elapsed * speed * 1.35) * amplitude * 0.55;
      array[i * 3 + 2] = z;
    }
    position.needsUpdate = true;
    object.geometry.computeVertexNormals();
  });
}

/**
 * Horizontaal kadevlak. Origin op het midden van het dek, op straatniveau.
 */
export function buildQuayDeck(length: number, depth: number = ENVIRONMENT.quay.depth): THREE.Mesh {
  const parts: Part[] = [
    part(box(length, 0.04, depth).translate(0, 0.02, 0), 'nightStone'),
    part(box(length, 0.06, 0.16).translate(0, 0.03, -depth / 2 + 0.08), 'pavement'),
    part(box(length, 0.08, 0.18).translate(0, 0.04, depth / 2 - 0.09), 'weatheredWood'),
    part(box(length, 0.035, 0.06).translate(0, 0.08, -depth / 2 + 0.42), 'slateGrey'),
    part(box(length, 0.035, 0.06).translate(0, 0.08, depth / 2 - 0.42), 'slateGrey'),
  ];

  for (let x = -length / 2 + 1.4; x <= length / 2 - 1.4; x += 2.8) {
    parts.push(part(box(0.72, 0.035, 0.08).translate(x, 0.11, -depth / 2 + 0.74), 'pavement'));
    parts.push(part(box(0.72, 0.035, 0.08).translate(x, 0.11, depth / 2 - 0.74), 'pavement'));
    parts.push(part(box(0.34, 0.1, 0.18).translate(x, 0.09, depth / 2 - 0.3), 'darkSteel'));
  }

  for (let x = -length / 2 + ENVIRONMENT.quay.slabModule; x < length / 2; x += ENVIRONMENT.quay.slabModule) {
    parts.push(part(box(0.045, 0.014, depth - 0.42).translate(x, 0.105, 0), 'charcoal'));
  }

  for (let x = -length / 2 + 3.6; x < length / 2 - 2; x += ENVIRONMENT.quay.mooringRingSpacing) {
    parts.push(part(box(0.62, 0.035, 0.08).translate(x, 0.13, depth / 2 - 0.18), 'darkSteel'));
    parts.push(part(box(0.08, 0.035, 0.42).translate(x, 0.131, depth / 2 - 0.34), 'darkSteel'));
  }

  for (let z = -depth / 2 + 1.1; z < depth / 2 - 0.8; z += 1.55) {
    parts.push(part(box(length - 0.8, 0.012, 0.04).translate(0, 0.106, z), 'charcoal'));
  }

  for (let x = -length / 2 + 4.2; x < length / 2 - 2; x += 9.6) {
    parts.push(part(box(1.2, 0.012, 0.08).translate(x, 0.115, -depth / 2 + 1.05), 'sodiumAmber'));
    parts.push(part(box(0.8, 0.012, 0.06).translate(x + 2.2, 0.115, depth / 2 - 0.9), 'deepTeal'));
  }

  const mesh = new THREE.Mesh(mergeParts(parts), vertexColored());
  mesh.name = 'NA_Env_QuayDeck';
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * Kademuur: de bakstenen rand tussen straat en water, met een houten stootrand.
 * Origin op het midden van de bovenkant, op straatniveau.
 */
export function buildQuayWall(length: number): THREE.Mesh {
  const height = ENVIRONMENT.quay.heightAboveWater;
  const thickness = ENVIRONMENT.quay.wallThickness;

  const parts: Part[] = [
    part(box(length, height, thickness).translate(0, -height / 2, 0), 'nightStone'),
    // Stootrand: een donkere houten balk net onder de kaderand, waar de boten tegenaan liggen.
    part(
      box(length, ENVIRONMENT.quay.bumperHeight, thickness + 0.12).translate(0, -0.45, 0),
      'weatheredWood',
    ),
    part(box(length, 0.08, thickness + 0.08).translate(0, -0.12, 0), 'slateGrey'),
    part(box(length, 0.08, thickness + 0.08).translate(0, -height + 0.18, 0), 'charcoal'),
  ];

  for (let x = -length / 2 + 1; x <= length / 2 - 1; x += 1.6) {
    parts.push(part(box(0.08, height * 0.76, 0.04).translate(x, -height * 0.48, thickness / 2 + 0.04), 'charcoal'));
  }

  const mesh = new THREE.Mesh(mergeParts(parts), vertexColored());
  mesh.name = 'NA_Env_QuayWall';
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * Lage kanaalbrug. Origin op het midden van het wegdek, op straatniveau.
 *
 * De brug loopt over de Z-as: breedte is X, overspanning is Z. Zo kan de LevelLoader hem
 * direct over een kanaal leggen zonder extra rotatie voor de meest voorkomende situatie.
 */
export function buildCanalBridge(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'NA_Env_CanalBridge';

  const b = ENVIRONMENT.bridge;
  const parts: Part[] = [
    part(box(b.width, b.deckThickness, b.span).translate(0, -b.deckThickness / 2, 0), 'nightStone'),
    part(box(b.width - b.curbWidth * 2, 0.03, b.span).translate(0, 0.035, 0), 'asphalt'),
    part(box(b.curbWidth, b.curbHeight, b.span).translate(-b.width / 2 + b.curbWidth / 2, b.curbHeight / 2, 0), 'pavement'),
    part(box(b.curbWidth, b.curbHeight, b.span).translate(b.width / 2 - b.curbWidth / 2, b.curbHeight / 2, 0), 'pavement'),
    part(
      box(b.railThickness, b.railThickness, b.span).translate(
        -b.width / 2 + b.curbWidth / 2,
        b.railHeight,
        0,
      ),
      'darkSteel',
    ),
    part(
      box(b.railThickness, b.railThickness, b.span).translate(
        b.width / 2 - b.curbWidth / 2,
        b.railHeight,
        0,
      ),
      'darkSteel',
    ),
    part(box(b.width - b.curbWidth * 2, 0.045, 0.12).translate(0, 0.03, -b.span / 2 + 0.7), 'slateGrey'),
    part(box(b.width - b.curbWidth * 2, 0.045, 0.12).translate(0, 0.03, b.span / 2 - 0.7), 'slateGrey'),
    part(box(0.18, 0.28, b.span + 0.32).translate(-b.width / 2 - 0.04, -0.18, 0), 'charcoal'),
    part(box(0.18, 0.28, b.span + 0.32).translate(b.width / 2 + 0.04, -0.18, 0), 'charcoal'),
    part(box(b.width + 0.36, 0.2, 0.28).translate(0, -0.22, -b.span / 2 - 0.08), 'nightStone'),
    part(box(b.width + 0.36, 0.2, 0.28).translate(0, -0.22, b.span / 2 + 0.08), 'nightStone'),
  ];

  for (const x of [-1, 1]) {
    for (let z = -b.span / 2 + b.railThickness / 2; z <= b.span / 2; z += b.postSpacing) {
      parts.push(
        part(
          box(b.railThickness, b.railHeight, b.railThickness).translate(
            x * (b.width / 2 - b.curbWidth / 2),
            b.railHeight / 2,
            z,
          ),
          'darkSteel',
        ),
      );
      parts.push(
        part(
          box(b.railThickness * 0.72, b.railThickness * 0.72, b.postSpacing * 0.58).translate(
            x * (b.width / 2 - b.curbWidth / 2),
            b.railHeight * 0.54,
            z + b.postSpacing * 0.28,
          ),
          'darkSteel',
        ),
      );
      parts.push(
        part(
          box(b.railThickness * 0.58, b.railThickness * 0.58, b.postSpacing * 0.46).rotateX(0.52).translate(
            x * (b.width / 2 - b.curbWidth / 2),
            b.railHeight * 0.32,
            z + b.postSpacing * 0.25,
          ),
          'darkSteel',
        ),
      );
    }
  }

  for (let z = -b.span / 2 + 1.1; z < b.span / 2 - 1; z += 2.4) {
    parts.push(part(box(b.width - b.curbWidth * 2.8, 0.014, 0.05).translate(0, 0.06, z), 'charcoal'));
  }

  const mesh = new THREE.Mesh(mergeParts(parts), vertexColored());
  mesh.name = 'shell';
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);

  return group;
}
